
import { useNavigate } from "react-router-dom";
import { 
  Table, TableBody, TableCaption, TableCell, 
  TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { StarRating } from "./StarRating";
import { RankIcon } from "./RankIcon";
import type { AirportLeaderboardStats } from "@/types/airport";
import { useTranslation } from "@/hooks/useTranslation";

interface DesktopLeaderboardProps {
  airportStats: AirportLeaderboardStats[];
  showWorst: boolean;
  isMobile: boolean;
}

export const DesktopLeaderboard = ({ airportStats, showWorst, isMobile }: DesktopLeaderboardProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleAirportClick = (airportName: string) => {
    // Encode the airport name to handle spaces and special characters in URLs
    const encodedAirportName = encodeURIComponent(airportName);
    navigate(`/airport/${encodedAirportName}`);
  };

  return (
    <Table>
      <TableCaption>
        {showWorst 
          ? t('bottom_10_airports')
          : t('top_10_airports')}
      </TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead className={isMobile ? "w-[8%]" : "w-[6%]"}></TableHead>
          <TableHead className={isMobile ? "w-[30%]" : "w-[30%]"}>{t('airport_name')}</TableHead>
          <TableHead className={isMobile ? "hidden" : "w-[20%]"}>{t('airport_location')}</TableHead>
          <TableHead className={`${isMobile ? "w-[32%]" : "w-[18%]"}`}>
            <div className="my-0 mx-0 px-[12px]">{t('score')}</div>
          </TableHead>
          <TableHead className={`${isMobile ? "w-[30%]" : "w-[16%]"} text-right`}>{t('success_rate')}</TableHead>
          <TableHead className={isMobile ? "hidden" : "w-[10%] text-right"}>{t('total_reviews')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody className="text-left">
        {airportStats.map((airport, index) => (
          <TableRow 
            key={airport.airport_id} 
            className="cursor-pointer hover:bg-muted/50" 
            onClick={() => handleAirportClick(airport.airport_name)}
          >
            <TableCell className="py-4">
              <RankIcon rank={index} isWorst={showWorst} />
            </TableCell>
            <TableCell className="font-medium max-w-xs">
              <div 
                className="line-clamp-1 hover:line-clamp-none pr-2" 
                title={t(airport.airport_name) || airport.airport_name}
              >
                {t(airport.airport_name) || airport.airport_name}
              </div>
            </TableCell>
            <TableCell className={isMobile ? "hidden" : ""}>
              <div className="break-words pr-2">
                {t(airport.airport_location) || airport.airport_location}
              </div>
            </TableCell>
            <TableCell className={isMobile ? "" : "text-right"}>
              <StarRating score={airport.average_score} isMobile={isMobile} />
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2 justify-end w-full">
                <Progress 
                  value={airport.hand_check_success_rate} 
                  className={`h-2 ${isMobile ? "w-[60px]" : "w-[100px]"} ${showWorst ? 'bg-muted' : ''}`} 
                />
                <span className={`text-sm ${isMobile ? "min-w-[30px]" : "min-w-[40px]"} text-right`}>
                  {airport.hand_check_success_rate.toFixed(0)}%
                </span>
              </div>
            </TableCell>
            <TableCell className={isMobile ? "hidden" : "text-right"}>
              <span className="min-w-[30px] inline-block">
                {airport.total_reviews}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
